import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, InputAdornment, lighten, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { BlacklistItem, BlacklistTypes } from './company-blacklist.types';

interface Props {
  blacklistType: BlacklistTypes;
  blacklistItems: BlacklistItem[];
  loading: boolean;

  handleRemoveSingleBlacklistItem?(identifier: string): () => void;
}

const BlacklistItemTable: React.FC<Props> = ({ blacklistType, blacklistItems = [], loading, handleRemoveSingleBlacklistItem }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const MAX_CONTACTS_PER_PAGE = 10;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = normalizedSearch
    ? blacklistItems.filter(
        (item) =>
          (item.companyName || '').toLowerCase().includes(normalizedSearch) ||
          (item.contactUrl || '').toLowerCase().includes(normalizedSearch)
      )
    : blacklistItems;

  const paginatedItems = filteredItems.slice((page - 1) * MAX_CONTACTS_PER_PAGE, page * MAX_CONTACTS_PER_PAGE);

  const handleChangePage = (event: any, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className={classes.list}>
      <div className={classes.searchBar}>
        <TextField
          size={'small'}
          fullWidth
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={
            blacklistType === BlacklistTypes.COMPANY
              ? t('searchBlacklistCompanies', 'Unternehmen suchen…')
              : t('searchBlacklistContacts', 'Kontakte suchen…')
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position={'start'}>
                <SearchIcon fontSize={'small'} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position={'end'}>
                <IconButton size={'small'} onClick={() => handleSearchChange('')}>
                  <ClearIcon fontSize={'small'} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </div>
      <div className={classes.listContent}>
        <TableContainer sx={{ height: '100%', width: '100%' }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                <TableCell sx={{ minWidth: '250px' }}>
                  {blacklistType === BlacklistTypes.COMPANY
                    ? t('companyName')
                    : blacklistType === BlacklistTypes.CONTACT
                    ? t('contactLinkedInUrl')
                    : '-'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 && normalizedSearch ? (
                <TableRow>
                  <TableCell>
                    <Typography variant={'body2'} color={'textSecondary'}>
                      {t('noBlacklistSearchResults', 'Keine Treffer für "{{search}}"', { search: search.trim() })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {paginatedItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                      {item.contactUrl ? (
                        <PaiLink
                          text={item.contactUrl}
                          size={'md'}
                          variant={'primary'}
                          underline={'always'}
                          href={item.contactUrl}
                          target={'_blank'}
                        />
                      ) : (
                        item.companyName || '-'
                      )}
                      {!_.isNil(handleRemoveSingleBlacklistItem) && blacklistType === BlacklistTypes.COMPANY && item.companyName ? (
                        <IconButton
                          className={classes.iconButton}
                          size={'small'}
                          onClick={handleRemoveSingleBlacklistItem(item.companyName)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null}
                      {!_.isNil(handleRemoveSingleBlacklistItem) && blacklistType === BlacklistTypes.CONTACT && item.contactUrl ? (
                        <IconButton
                          className={classes.iconButton}
                          size={'small'}
                          onClick={handleRemoveSingleBlacklistItem(item.contactUrl)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Stack className={classes.listFooter} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <PaiPagination total={filteredItems.length} itemsPerPage={MAX_CONTACTS_PER_PAGE} page={page} handleChangePage={handleChangePage} />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  list: {
    height: '300px',
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  listFooter: {
    padding: '10.5px 40px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
  listTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  searchBar: {
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  listContent: {
    position: 'relative',
    height: 'calc(100% - 47px - 57px)',
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
      '& mark': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.8),
      },
    },
  },
  iconButton: {
    padding: '4px',
    '& svg': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
}));

export default BlacklistItemTable;
